import { batch } from 'react-redux'
import { customizeError, downloadFile, fileToBase64, formatDate, generateBlob } from '@chilecompra/react-kit'
import { REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_UTM_VALIDATION } from '../../config/settings/environment'
import { SANC_DOCUMENT_TYPES } from '../../config/settings/constant'
import { makeActionCreator } from '../../config/store/utils'
import { checkIfForeignDni, cleanDNI } from '../../modules/utils/helpers'
import { downloadSanctionFile, getAccessTokenGesintel, uploadFile } from '../../services/attachments'
import {
  downloadRevokeFile,
  getAttachmentsTypes,
  getPOBiddingInfo,
  getSanctionDetails,
  getSanctionsTypes,
  getUserInfo,
  postRebokeRequest,
  registerSanction,
  removeSanction
} from '../../services/sanction'

export const SET_INITIAL_STATE = 'SET_INITIAL_STATE'
export const onSetInitialState = makeActionCreator(SET_INITIAL_STATE)

export const SET_FORM_DATA = 'SET_FORM_DATA'
export const onSetFormData = makeActionCreator(SET_FORM_DATA)

export const GET_USER_INFO = 'GET_USER_INFO'
export const GET_USER_INFO_ERROR = 'GET_USER_INFO_ERROR'
export const GET_USER_INFO_SUCCESS = 'GET_USER_INFO_SUCCESS'
export const onGetUserInfo = makeActionCreator(GET_USER_INFO)
export const onGetUserInfoError = makeActionCreator(GET_USER_INFO_ERROR, 'payload')
export const onGetUserInfoSuccess = makeActionCreator(GET_USER_INFO_SUCCESS, 'payload')
export const onGetUserInfoThunk = () => async dispatch => {
  dispatch(onGetUserInfo())

  try {
    const { data } = await getUserInfo({ actionType: GET_USER_INFO })

    return dispatch(
      onGetUserInfoSuccess({
        orgName: data?.payload?.nombreorganismo,
        fullName: data?.payload?.nombreusuario,
        position: data?.payload?.cargousuario,
        phone: data?.payload?.telefono,
        email: data?.payload?.email
      })
    )
  } catch (error) {
    return dispatch(
      onGetUserInfoError({
        error: customizeError({
          originalError: error?.originalError,
          code: error?.code,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}

export const GET_PO_BIDDING_INFO = 'GET_PURCHASE_ORDER_INFO'
export const GET_PO_BIDDING_INFO_ERROR = 'GET_PO_BIDDING_INFO_ERROR'
export const GET_PO_BIDDING_INFO_SUCCESS = 'GET_PO_BIDDING_INFO_SUCCESS'
export const onGetPOBiddingInfo = makeActionCreator(GET_PO_BIDDING_INFO)
export const onGetPOBiddingInfoError = makeActionCreator(GET_PO_BIDDING_INFO_ERROR, 'payload')
export const onGetPOBiddingInfoSuccess = makeActionCreator(GET_PO_BIDDING_INFO_SUCCESS, 'payload')
export const onGetPOBiddingInfoThunk = payload => async dispatch => {
  dispatch(onGetPOBiddingInfo())
  const { externalCode, dni, processType } = payload

  try {
    const { data } = await getPOBiddingInfo({
      actionType: GET_PO_BIDDING_INFO,
      externalCode,
      dni,
      processType: processType === 'PO' ? 'ordencompra' : 'licitacion'
    })
    const {
      payload: { tienecontrato: hasContract, estado: poState, montoutm: amountUTM }
    } = data

    if (
      (processType === 'PO' && poState.toUpperCase() !== 'ACEPTADA') ||
      (processType === 'BID' && poState.toUpperCase() !== 'ADJUDICADA')
    ) {
      throw customizeError({ originalError: {}, code: 400, reason: 'IS_NOT_ACCEPTED_STATUS_ERROR' })
    }

    if (
      REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_UTM_VALIDATION === true ||
      REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_UTM_VALIDATION === 'true'
    ) {
      if (parseInt(amountUTM, 10) > 1000) {
        throw customizeError({ originalError: {}, code: 400, reason: 'AMOUNT_MUST_BE_LESS_THAN_1000_ERROR' })
      }
    }

    if (
      REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_UTM_VALIDATION === true ||
      REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_UTM_VALIDATION === 'true'
    ) {
      if (hasContract) {
        throw customizeError({ originalError: {}, code: 400, reason: 'PO_HAS_CONTRACT_ERROR' })
      }
    }

    return dispatch(
      onGetPOBiddingInfoSuccess({
        id: data?.payload?.id,
        code: data?.payload?.codigo,
        name: data?.payload?.nombre,
        shippingDate:
          (data?.payload?.fechaenvio && formatDate(data?.payload?.fechaenvio, 'dd/MM/yyyy')) || 'Sin información',
        acceptanceDate:
          (data?.payload?.fechaaceptacion && formatDate(data?.payload?.fechaaceptacion, 'dd/MM/yyyy')) ||
          'Sin información',
        openingDate:
          (data?.payload?.fechaapertura && formatDate(data?.payload?.fechaapertura, 'dd/MM/yyyy')) || 'Sin información',
        closingDate:
          (data?.payload?.fechacierre && formatDate(data?.payload?.fechacierre, 'dd/MM/yyyy')) || 'Sin información',
        origin: data?.payload?.origen,
        originName: data?.payload?.nombreorigen,
        amountUTM: parseInt(amountUTM, 10),
        state: poState,
        hasContract,
        entCode: data?.payload?.entCodeProveedor,
        isPM: data?.payload?.esJP || false,
        processType
      })
    )
  } catch (error) {
    let reason
    switch (error.code) {
      case 400:
        reason = error.reason
        break
      case 401:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
      case 403:
        if (error?.originalError?.response?.data?.errores[0]?.codigo === '403-1') {
          reason = 'DOES_NOT_BELONG_BUYER_ERROR'
        } else if (error?.originalError?.response?.data?.errores[0]?.codigo === '403-6') {
          reason = 'BOSS_ENTER_SANCTION_CM_ERROR'
        } else {
          reason = 'DOES_NOT_BELONG_SELLER_ERROR'
        }
        break
      case 404:
        reason = 'PO_DOES_NOT_EXIST_ERROR'
        break
      default:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
    }

    return dispatch(
      onGetPOBiddingInfoError({
        error: customizeError({ originalError: error?.originalError, code: error?.code, reason }).toObject()
      })
    )
  }
}

export const GET_SANCTIONS_TYPES = 'GET_SANCTIONS_TYPES'
export const GET_SANCTIONS_TYPES_ERROR = 'GET_SANCTIONS_TYPES_ERROR'
export const GET_SANCTIONS_TYPES_SUCCESS = 'GET_SANCTIONS_TYPES_SUCCESS'
export const onGetSantionsTypes = makeActionCreator(GET_SANCTIONS_TYPES)
export const onGetSantionsTypesError = makeActionCreator(GET_SANCTIONS_TYPES_ERROR, 'payload')
export const onGetSantionsTypesSuccess = makeActionCreator(GET_SANCTIONS_TYPES_SUCCESS, 'payload')
export const onGetSantionsTypesThunk = () => async (dispatch, getState) => {
  dispatch(onGetSantionsTypes())

  try {
    const { data } = await getSanctionsTypes({ actionType: GET_SANCTIONS_TYPES })
    const {
      poBiddingInfo: { origin, isPM }
    } = getState().sanction

    const sanctionsTypes = data?.payload
      ?.map(type => {
        return {
          name: type.concepto,
          value: type.codigosancion
        }
      })
      .filter(type => {
        if (type.value === 'AM' && !(origin === 'LC' || (origin === 'CM' && isPM))) {
          return false
        }
        if (type.value === 'BG' && origin === 'CA') {
          return false
        }
        if (type.value === 'BP' && !isPM) {
          return false
        }
        return true
      })

    return dispatch(
      onGetSantionsTypesSuccess({
        sanctionsTypes,
        defaultSanctionType: (sanctionsTypes || [])[0]?.value || ''
      })
    )
  } catch (error) {
    return dispatch(
      onGetSantionsTypesError({
        error: customizeError({
          originalError: error?.originalError,
          code: error?.code,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}

export const GET_ATTACHMENTS_TYPES = 'GET_ATTACHMENTS_TYPES'
export const GET_ATTACHMENTS_TYPES_ERROR = 'GET_ATTACHMENTS_TYPES_ERROR'
export const GET_ATTACHMENTS_TYPES_SUCCESS = 'GET_ATTACHMENTS_TYPES_SUCCESS'
export const onGetAttachmentsTypes = makeActionCreator(GET_ATTACHMENTS_TYPES)
export const onGetAttachmentsTypesError = makeActionCreator(GET_ATTACHMENTS_TYPES_ERROR, 'payload')
export const onGetAttachmentsTypesSuccess = makeActionCreator(GET_ATTACHMENTS_TYPES_SUCCESS, 'payload')
export const onGetAttachmentsTypesThunk = () => async dispatch => {
  dispatch(onGetAttachmentsTypes())

  try {
    const { data } = await getAttachmentsTypes({
      actionType: GET_ATTACHMENTS_TYPES
    })

    const attachmentsTypes = data?.payload?.map(type => {
      return {
        name: type.nombre,
        value: type.codigo
      }
    })

    return dispatch(
      onGetAttachmentsTypesSuccess({
        attachmentsTypes,
        defaultAttachmentType: (attachmentsTypes || [])[0]?.value || ''
      })
    )
  } catch (error) {
    return dispatch(
      onGetAttachmentsTypesError({
        error: customizeError({
          originalError: error?.originalError,
          code: error?.code,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}

export const UPLOAD_SANCTION_ATTACHMENT = 'UPLOAD_SANCTION_ATTACHMENT'
export const UPLOAD_SANCTION_ATTACHMENT_ERROR = 'UPLOAD_SANCTION_ATTACHMENT_ERROR'
export const UPLOAD_SANCTION_ATTACHMENT_SUCCESS = 'UPLOAD_SANCTION_ATTACHMENT_SUCCESS'
export const onUploadSanctionAttachment = makeActionCreator(UPLOAD_SANCTION_ATTACHMENT)
export const onUploadSanctionAttachmentError = makeActionCreator(UPLOAD_SANCTION_ATTACHMENT_ERROR, 'payload')
export const onUploadSanctionAttachmentSuccess = makeActionCreator(UPLOAD_SANCTION_ATTACHMENT_SUCCESS, 'payload')
export const onUploadSanctionAttachmentThunk = payload => async (dispatch, getState) => {
  dispatch(onUploadSanctionAttachment())
  const { dni, callback } = payload

  try {
    const isForeign = checkIfForeignDni(dni)
    const { orgLegalName, sellerType } = getState().basicSellerInformation
    const {
      form: {
        attachment: { file, fileName },
        sanctionType
      },
      poBiddingInfo: { entCode }
    } = getState().sanction

    const {
      data: { payload: token }
    } = await getAccessTokenGesintel()

    const result = await fileToBase64(file).catch(() => {
      throw customizeError({ originalError: {}, code: 500, reason: 'COULD_NOT_UPLOAD_FILE_ERROR' })
    })

    const { data } = await uploadFile({
      actionType: UPLOAD_SANCTION_ATTACHMENT,
      base64File: result.replace('data:', '').replace(/^.+,/, ''),
      entCode,
      fileName,
      id: isForeign ? dni : cleanDNI(dni.toString()).replace('-', ''),
      orgLegalName,
      sellerType: sellerType === 'natural' ? 'Person' : 'Entity',
      token,
      type: SANC_DOCUMENT_TYPES[sanctionType]
    })

    if (data?.status !== 'OK') {
      throw customizeError({ originalError: {}, code: 500, reason: 'COULD_NOT_UPLOAD_FILE_ERROR' })
    }

    return dispatch(onRegisterSanctionThunk({ dni, callback, docFolio: parseInt(data?.folio, 10) }))
  } catch (error) {
    return batch(() => {
      dispatch(
        onUploadSanctionAttachmentError({
          error: customizeError({
            originalError: error?.originalError,
            code: error?.code,
            reason: 'COULD_NOT_UPLOAD_FILE_ERROR'
          }).toObject()
        })
      )
      callback()
    })
  }
}

export const REGISTER_SANCTION = 'REGISTER_SANCTION'
export const REGISTER_SANCTION_ERROR = 'REGISTER_SANCTION_ERROR'
export const REGISTER_SANCTION_SUCCESS = 'REGISTER_SANCTION_SUCCESS'
export const onRegisterSanction = makeActionCreator(REGISTER_SANCTION)
export const onRegisterSanctionError = makeActionCreator(REGISTER_SANCTION_ERROR, 'payload')
export const onRegisterSanctionSuccess = makeActionCreator(REGISTER_SANCTION_SUCCESS, 'payload')
export const onRegisterSanctionThunk = payload => async (dispatch, getState) => {
  dispatch(onRegisterSanction())
  const { dni, docFolio, callback } = payload

  try {
    const isForeign = checkIfForeignDni(dni)
    const {
      form: {
        attachment: { fileName },
        attachmentType,
        backupDate,
        reason,
        resolutionNumber,
        sanctionType
      },
      poBiddingInfo: { code, id, processType }
    } = getState().sanction

    const { data } = await registerSanction({
      actionType: REGISTER_SANCTION,
      attachmentType,
      backupDate: `${formatDate(backupDate, 'yyyy-MM-dd')}T00:00:00Z`,
      dni,
      docFolio,
      fileName,
      id,
      isForeign: isForeign ? 1 : 0,
      externalCode: code,
      reason,
      resolutionNumber,
      sanctionType,
      processType: processType === 'PO' ? 'OC' : 'LC'
    })

    return batch(() => {
      dispatch(
        onRegisterSanctionSuccess({
          sanctionID: data?.payload?.idsancion
        })
      )
      callback()
    })
  } catch (error) {
    const errorDetails = {
      reason: 'SOMETHING_WENT_WRONG_ERROR',
      originalError: null
    }

    if (error.code === 409) {
      const errorResponse = error?.originalError?.response?.data?.errores[0]

      if (errorResponse?.codigo === '409-1') {
        errorDetails.reason = 'PO_HAS_CONTRACT_ERROR'
        errorDetails.originalError = { message: errorResponse.descripcion }
      }
    }

    const customError = customizeError({
      originalError: errorDetails.originalError || error?.originalError,
      code: error?.code,
      reason: errorDetails.reason
    })

    return batch(() => {
      dispatch(onUploadSanctionAttachmentError({ error: customError.toObject() }))
      callback()
    })
  }
}

export const SELECT_SANCTION_ATTACHMENT = 'SELECT_SANCTION_ATTACHMENT'
export const onSelectSanctionAttachment = makeActionCreator(SELECT_SANCTION_ATTACHMENT, 'payload')

export const UPDATE_FORM_VALUE = 'UPDATE_FORM_VALUE'
export const onUpdateFormValue = makeActionCreator(UPDATE_FORM_VALUE, 'payload')

export const CLOSE_FEEDBACK_DIALOG = 'CLOSE_FEEDBACK_DIALOG'
export const onCloseFeedbackDialogThunk = makeActionCreator(CLOSE_FEEDBACK_DIALOG)

export const GET_SANCTION_DETAILS = 'GET_SANCTION_DETAILS'
export const GET_SANCTION_DETAILS_ERROR = 'GET_SANCTION_DETAILS_ERROR'
export const GET_SANCTION_DETAILS_SUCCESS = 'GET_SANCTION_DETAILS_SUCCESS'
export const onGetSantionDetails = makeActionCreator(GET_SANCTION_DETAILS)
export const onGetSantionDetailsError = makeActionCreator(GET_SANCTION_DETAILS_ERROR, 'payload')
export const onGetSantionDetailsSuccess = makeActionCreator(GET_SANCTION_DETAILS_SUCCESS, 'payload')
export const onGetSantionDetailsThunk = payload => async dispatch => {
  dispatch(onGetSantionDetails())

  try {
    const { data } = await getSanctionDetails({
      actionType: GET_SANCTION_DETAILS,
      id: payload.id
    })

    const userInfo = {
      orgName: data?.payload?.comprador?.nombreorganismo || '',
      unitName: data?.payload?.comprador?.nombreunidad || '',
      fullName: data?.payload?.comprador?.nombreusuario || '',
      position: data?.payload?.comprador?.cargousuario || '',
      phone: data?.payload?.comprador?.telefono || '',
      email: data?.payload?.comprador?.email || ''
    }

    const user = data?.payload?.adjuntos[0]?.usuario || ''
    const isMigratedFile = user === '-1' || user === '-2' || user === '-3'
    const resolutionNumber = isMigratedFile ? 'Sin información' : data?.payload?.adjuntos[0]?.nroDocumento || ''

    return dispatch(
      onGetSantionDetailsSuccess({
        canWithdraw: false,
        codeSanction: data?.payload?.codigoSancion,
        sanctionID: data?.payload?.idSancion,
        canRevoke: data?.payload?.puedeRetirar,
        retiroVigente: data?.payload?.retiroVigente,
        attachmentsWS: (data?.payload?.adjuntosWS || []).map(item => ({
          atcCode: item.atcCode,
          fileName: item.nombreArchivo || 'Documento de respaldo',
          documentType: item.tipoDocumento,
          date: item.fechaDocumento
        })),
        form: {
          attachment: {
            docFolio: data?.payload?.adjuntos[0]?.docFolio,
            file: null,
            fileName: data?.payload?.adjuntos[0]?.nombreArchivo || '',
            applicationType: '',
            isMigrated: isMigratedFile
          },
          attachmentType: data?.payload?.adjuntos[0]?.tipoDocumento || '',
          backupDate: data?.payload?.adjuntos[0]?.fechaDocumento,
          isSGC: data?.payload?.esSGC,
          reason: data?.payload?.motivoSancion,
          resolutionNumber,
          sanctionType: data?.payload?.tipoSancion || '',
          contractUrl: data?.payload?.urlContrato || ''
        },
        poBiddingInfo: {
          code: data?.payload?.codigoProceso,
          name: data?.payload?.nombreProceso,
          shippingDate:
            (data?.payload?.fechaEnvio && formatDate(data?.payload?.fechaEnvio, 'dd/MM/yyyy')) || 'Sin información',
          acceptanceDate:
            (data?.payload?.fechaAceptacion && formatDate(data?.payload?.fechaAceptacion, 'dd/MM/yyyy')) ||
            'Sin información',
          openingDate:
            (data?.payload?.fechaApertura && formatDate(data?.payload?.fechaApertura, 'dd/MM/yyyy')) ||
            'Sin información',
          closingDate:
            (data?.payload?.fechaCierre && formatDate(data?.payload?.fechaCierre, 'dd/MM/yyyy')) || 'Sin información',
          processType: data?.payload?.tipoProceso === 'OC' ? 'PO' : 'BID',
          entCode: data?.payload?.entcodeProveedor
        },
        userInfo: data?.payload?.comprador === null ? undefined : userInfo
      })
    )
  } catch (error) {
    if (error?.originalError?.code === 'ERR_CANCELED') return

    let reason
    switch (error.code) {
      case 404:
        reason = 'DOES_NOT_EXIST_ERROR'
        break
      default:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
    }

    return dispatch(
      onGetSantionDetailsError({
        error: customizeError({ originalError: error?.originalError, code: error?.code, reason }).toObject()
      })
    )
  }
}

export const DOWNLOAD_SANCTION_FILE = 'DOWNLOAD_SANCTION_FILE'
export const DOWNLOAD_SANCTION_FILE_ERROR = 'DOWNLOAD_SANCTION_FILE_ERROR'
export const DOWNLOAD_SANCTION_FILE_SUCCESS = 'DOWNLOAD_SANCTION_FILE_SUCCESS'
export const onDownloadSanctionFile = makeActionCreator(DOWNLOAD_SANCTION_FILE)
export const onDownloadSanctionFileError = makeActionCreator(DOWNLOAD_SANCTION_FILE_ERROR, 'payload')
export const onDownloadSanctionFileSuccess = makeActionCreator(DOWNLOAD_SANCTION_FILE_SUCCESS, 'payload')
export const onDownloadSanctionFileThunk = () => async (dispatch, getState) => {
  dispatch(onDownloadSanctionFile())

  try {
    const {
      form: {
        attachment: { docFolio, file, fileName }
      }
    } = getState().sanction
    if (file !== null) {
      downloadFile(file, fileName)
      return dispatch(
        onDownloadSanctionFileSuccess({
          file
        })
      )
    }

    const { data, headers } = await downloadSanctionFile({ actionType: DOWNLOAD_SANCTION_FILE, docFolio })
    const blob = generateBlob(data, headers['content-type'])
    downloadFile(blob, fileName)

    return dispatch(
      onDownloadSanctionFileSuccess({
        file: blob
      })
    )
  } catch (error) {
    return dispatch(
      onDownloadSanctionFileError({
        error: customizeError({
          originalError: error?.originalError,
          code: error?.code,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}

export const DOWNLOAD_REVOKE_FILE = 'DOWNLOAD_REVOKE_FILE'
export const DOWNLOAD_REVOKE_FILE_ERROR = 'DOWNLOAD_REVOKE_FILE_ERROR'
export const DOWNLOAD_REVOKE_FILE_SUCCESS = 'DOWNLOAD_REVOKE_FILE_SUCCESS'
export const onDownloadRevokeFile = makeActionCreator(DOWNLOAD_REVOKE_FILE, 'payload')
export const onDownloadRevokeFileError = makeActionCreator(DOWNLOAD_REVOKE_FILE_ERROR, 'payload')
export const onDownloadRevokeFileSuccess = makeActionCreator(DOWNLOAD_REVOKE_FILE_SUCCESS, 'payload')
export const onDownloadRevokeFileThunk = payload => async dispatch => {
  const { atcCode, fileName } = payload
  dispatch(onDownloadRevokeFile({ atcCode }))

  try {
    const { data, headers } = await downloadRevokeFile({ actionType: DOWNLOAD_REVOKE_FILE, atcCode })
    const blob = generateBlob(data, headers['content-type'])
    downloadFile(blob, fileName)

    return dispatch(onDownloadRevokeFileSuccess({ atcCode }))
  } catch (error) {
    return dispatch(
      onDownloadRevokeFileError({
        error: customizeError({
          originalError: error?.originalError,
          code: error?.code,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}

export const WITHDRAW_SANCTION = 'WITHDRAW_SANCTION'
export const WITHDRAW_SANCTION_ERROR = 'WITHDRAW_SANCTION_ERROR'
export const WITHDRAW_SANCTION_SUCCESS = 'WITHDRAW_SANCTION_SUCCESS'
export const onWithdrawSanction = makeActionCreator(WITHDRAW_SANCTION)
export const onWithdrawSanctionError = makeActionCreator(WITHDRAW_SANCTION_ERROR, 'payload')
export const onWithdrawSanctionSuccess = makeActionCreator(WITHDRAW_SANCTION_SUCCESS)
export const onWithdrawSanctionThunk = payload => async dispatch => {
  dispatch(onWithdrawSanction())
  const { id, callback, justification } = payload

  try {
    await removeSanction({ actionType: WITHDRAW_SANCTION, id, justification })

    return batch(() => {
      dispatch(onWithdrawSanctionSuccess())
      callback()
    })
  } catch (error) {
    return batch(() => {
      dispatch(
        onWithdrawSanctionError({
          error: customizeError({
            originalError: error?.originalError,
            code: error?.code,
            reason: 'SOMETHING_WENT_WRONG_ERROR'
          }).toObject()
        })
      )
      callback()
    })
  }
}

export const OPEN_REVOKE_DIALOG_SANCTION = 'OPEN_REVOKE_DIALOG_SANCTION'
export const CLOSE_REVOKE_DIALOG_SANCTION = 'CLOSE_REVOKE_DIALOG_SANCTION'
export const onOpenRevokeDialogSanction = makeActionCreator(OPEN_REVOKE_DIALOG_SANCTION)
export const onCloseRevokeDialogSanction = makeActionCreator(CLOSE_REVOKE_DIALOG_SANCTION)

export const UPLOAD_REVOKE_SANCTION = 'UPLOAD_REVOKE_SANCTION'
export const UPLOAD_REVOKE_SANCTION_ERROR = 'UPLOAD_REVOKE_SANCTION_ERROR'
export const UPLOAD_REVOKE_SANCTION_SUCCESS = 'UPLOAD_REVOKE_SANCTION_SUCCESS'
export const onUploadAttachmentRevokeSanction = makeActionCreator(UPLOAD_REVOKE_SANCTION)
export const onUploadAttachmentRevokeSanctionError = makeActionCreator(UPLOAD_REVOKE_SANCTION_ERROR, 'payload')
export const onUploadAttachmentRevokeSanctionSuccess = makeActionCreator(UPLOAD_REVOKE_SANCTION_SUCCESS, 'payload')
export const onUploadAttachmentRevokeSanctionThunk = payload => async (dispatch, getState) => {
  dispatch(onUploadAttachmentRevokeSanction())
  const { dni, attachmentRevoke, callback, callbackError } = payload

  try {
    const isForeign = checkIfForeignDni(dni)

    const { orgLegalName, sellerType } = getState().basicSellerInformation
    const {
      codeSanction,
      poBiddingInfo: { entCode }
    } = getState().sanction

    const {
      data: { payload: token }
    } = await getAccessTokenGesintel()

    const result = await fileToBase64(attachmentRevoke.file).catch(() => {
      throw customizeError({ originalError: {}, code: 500, reason: 'COULD_NOT_UPLOAD_FILE_ERROR' })
    })

    const { data } = await uploadFile({
      actionType: UPLOAD_REVOKE_SANCTION,
      base64File: result.replace('data:', '').replace(/^.+,/, ''),
      fileName: attachmentRevoke.name,
      id: isForeign ? dni : cleanDNI(dni.toString()).replace('-', ''),
      orgLegalName,
      sellerType: sellerType === 'natural' ? 'Person' : 'Entity',
      token,
      entCode,
      type: SANC_DOCUMENT_TYPES[codeSanction]
    })

    if (data?.status !== 'OK') {
      throw customizeError({ originalError: {}, code: 500, reason: 'COULD_NOT_UPLOAD_FILE_ERROR' })
    }

    callback && callback(data?.folio)

    return dispatch(onUploadAttachmentRevokeSanctionSuccess())
  } catch (error) {
    return batch(() => {
      dispatch(
        onUploadAttachmentRevokeSanctionError({
          error: customizeError({
            originalError: error?.originalError,
            code: error?.code,
            reason: 'COULD_NOT_UPLOAD_FILE_ERROR'
          }).toObject()
        })
      )
      callbackError && callbackError()
    })
  }
}

export const APPLY_REVOKE_SANCTION = 'APPLY_REVOKE_SANCTION'
export const APPLY_REVOKE_SANCTION_ERROR = 'APPLY_REVOKE_SANCTION_ERROR'
export const APPLY_REVOKE_SANCTION_SUCCESS = 'APPLY_REVOKE_SANCTION_SUCCESS'
export const onApplyRevokeSanction = makeActionCreator(APPLY_REVOKE_SANCTION)
export const onApplyRevokeSanctionError = makeActionCreator(APPLY_REVOKE_SANCTION_ERROR, 'payload')
export const onApplyRevokeSanctionSuccess = makeActionCreator(APPLY_REVOKE_SANCTION_SUCCESS, 'payload')
export const onApplyRevokeSanctionThunk = payload => async dispatch => {
  dispatch(onApplyRevokeSanction())

  try {
    const data = await postRebokeRequest(payload.reboke)

    payload?.callback && payload.callback()

    return dispatch(
      onApplyRevokeSanctionSuccess({
        reloadSanction: true,
        ...data
      })
    )
  } catch (error) {
    return batch(() => {
      payload?.callbackError && payload.callbackError()

      dispatch(
        onApplyRevokeSanctionError({
          error: customizeError({
            originalError: error?.originalError,
            code: error?.code,
            reason: 'COULD_NOT_UPLOAD_FILE_ERROR'
          }).toObject()
        })
      )
    })
  }
}
